import {useEffect, useMemo, useState} from "preact/compat";
import {useMediaQuery} from "../../../hooks";

// Self-explanatory
const TOTAL_SLIDES = 3;
// Slides in which the "close" button must be at the light style state
// const LIGHT_CLOSE_BUTTON_SLIDES = [2, 4];
const LIGHT_CLOSE_BUTTON_SLIDES = [3, 4];
// Slide changers events
const EVENT_LISTENERS = ['keydown', 'wheel'];

let xDown = null;
let yDown = null;

// Scroll debouncer
let isLocked = false;

export const useContent = (props) => {
    // Destructured props
    const {handleButtonStyle} = props;

    // State to control the "Continuar Comprando" button display state
    const [showShoppingButton, setShowShoppingButton] = useState(true);
    // State to control the current slide
    const [currentSlide, setCurrentSlide] = useState(1);
    // State to control the slide stage feature
    const [thirdSlideStage, setThirdSlideStage] = useState(1);

    // Self-explanatory
    const isLastSlide = useMemo(() => {
        return currentSlide === TOTAL_SLIDES + 1;
    }, [currentSlide]);

    // Simple media query hook
    const isMobile = useMediaQuery("(max-width: 767px)");
    const isXSViewport = useMediaQuery("(max-width: 576px)");

    // Procedure to handle the third slide stage
    const handleThirdSlideStage = (state) => {
        setThirdSlideStage(state);
    }

    // Procedure to handle the "currentSlide" state
    const handleCurrentSlide = (action = "next") => {
        if (isLocked) {
            return;
        }

        if (action === "next" && currentSlide === 2 && thirdSlideStage < 3) {
            setThirdSlideStage(2);

            // Timer to automatically switch to the third stage
            const timer = setTimeout(() => {
                setThirdSlideStage(3);

                clearTimeout(timer);
            }, 1500);

            return;
        }

        setCurrentSlide(prevState => {
            switch (action) {
                case "next": {
                    if (prevState === TOTAL_SLIDES + 1) {
                        return prevState;
                    }

                    return prevState + 1;
                }
                case "prev": {
                    if (prevState === 1) {
                        return prevState;
                    }

                    return prevState - 1;
                }
            }
        });

        isLocked = true;

        const timer = setTimeout(() => {
            isLocked = false;

            clearTimeout(timer);
        }, 700);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const slideChangeHandler = (event) => {
        if (event.key === 'ArrowDown' || event?.deltaY && event?.deltaY > 0) {
            handleCurrentSlide();
        } else if (event.key === 'ArrowUp' || event?.deltaY && event?.deltaY < 0) {
            handleCurrentSlide("prev");
        }
    };

    // Procedure to control the "showShoppingButton" state
    const handleShowShoppingButton = (state = undefined) => {
        setShowShoppingButton(prevState => {
            return typeof state !== "undefined" ? state : !prevState;
        });
    }

    function getTouches(evt) {
        return evt.touches ||          // browser API
            evt.originalEvent.touches; // jQuery
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function handleTouchStart(evt) {
        const firstTouch = getTouches(evt)[0];

        xDown = firstTouch.clientX;
        yDown = firstTouch.clientY;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    function handleTouchMove(evt) {
        if (!xDown || !yDown) {
            return;
        }

        let yUp = evt.touches[0].clientY;
        let yDiff = yDown - yUp;

        handleCurrentSlide(yDiff > 0 ? "next" : "prev");

        xDown = null;
        yDown = null;
    }

    // Effect to hide the main "Continuar Comprando" button and do some more layout stuff
    useEffect(() => {
        // Switches between light and dark "close" button styles
        handleButtonStyle(
            (LIGHT_CLOSE_BUTTON_SLIDES.includes(currentSlide) && isMobile) ? "light" : "dark"
        );

        // If the bellow condition is truthy,
        // then it means the user has reached the last slide
        if (currentSlide !== TOTAL_SLIDES + 1) {
            handleShowShoppingButton(currentSlide < TOTAL_SLIDES + 1);

            return;
        }

        handleShowShoppingButton(isXSViewport);
    }, [
        currentSlide,
        isXSViewport
    ]);

    // Effect to reset any desired component
    useEffect(() => {
        if (currentSlide > 2) {
            setThirdSlideStage(1);
        }
    }, [currentSlide]);

    // Effect to control the event listeners
    useEffect(() => {
        // Event triggers to switch to the next slide
        EVENT_LISTENERS.forEach((key) => {
            document.addEventListener(key, slideChangeHandler);
        });

        document.addEventListener('touchstart', handleTouchStart, false);
        document.addEventListener('touchmove', handleTouchMove, false);

        return () => {
            EVENT_LISTENERS.forEach((key) => {
                document.removeEventListener(key, slideChangeHandler);
            });

            document.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
        }
    }, [
        currentSlide,
        handleTouchMove,
        handleTouchStart,
        slideChangeHandler
    ]);

    return {
        TOTAL_SLIDES,
        showShoppingButton,
        currentSlide,
        isMobile,
        isLastSlide,
        thirdSlideStage,
        handleThirdSlideStage,
        handleCurrentSlide,
        handleShowShoppingButton
    }
}
